import { ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react'

import { ChatMarkdown } from '@/components/Chat/ChatMarkdown'
import { cn } from '@/lib/utils'
import { SafetyRating, type SafetyScore } from '@/models/salesLead'

const ratingConfig: Record<SafetyRating, { icon: typeof ShieldAlert; chipClass: string; iconClass: string }> =
  {
    [SafetyRating.GOOD]: {
      icon: ShieldCheck,
      chipClass: 'bg-green-100 text-green-800',
      iconClass: 'text-green-600',
    },
    [SafetyRating.MODERATE]: {
      icon: ShieldAlert,
      chipClass: 'bg-amber-100 text-amber-800',
      iconClass: 'text-amber-600',
    },
    [SafetyRating.POOR]: {
      icon: ShieldX,
      chipClass: 'bg-red-100 text-red-800',
      iconClass: 'text-red-600',
    },
  }

export function SafetySection({ safetyScore }: { safetyScore: SafetyScore | null }) {
  if (!safetyScore) {
    return <div className="text-muted-foreground text-sm">No safety score yet.</div>
  }
  const cfg = ratingConfig[safetyScore.rating]
  const RatingIcon = cfg.icon
  // The rating chip already shows POOR/MODERATE/GOOD, so drop a leading
  // "POOR — " the model often repeats at the start of the headline.
  const headline = safetyScore.headline.replace(new RegExp(`^\\s*${safetyScore.rating}\\b[\\s—–-]*`, 'i'), '')
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <RatingIcon className={cn('size-12 shrink-0', cfg.iconClass)} />
        <div className="flex flex-col items-start gap-1">
          <span className={cn('rounded px-2.5 py-1 text-sm font-medium', cfg.chipClass)}>
            {safetyScore.rating}
          </span>
          <p className="text-sm font-medium">{headline}</p>
        </div>
      </div>
      <div className="safety-smmary mt-2 text-xs">
        <ChatMarkdown content={safetyScore.summary} />
      </div>
      {safetyScore.generatedAt && (
        <p className="text-muted-foreground text-xs">
          Updated {new Date(safetyScore.generatedAt).toLocaleString()}
        </p>
      )}

      <style>
        {`

          .safety-smmary p  ,
          .safety-smmary ul  ,
          .safety-smmary ol  {
            font-size: 0.85rem;
          }
          .safety-smmary p {
            margin-top: 1rem;
            margin-bottom: 1rem;
          }
          .safety-smmary ul {
            margin-top: 1rem;
            margin-bottom: 1rem;
          }

          .safety-smmary li {
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
          }

          .safety-smmary p + ul {
            margin-top: 0;
          }

          .safety-smmary p:has(+ ul) {
            margin-bottom: 0.2em;
          }


          `}
      </style>
    </div>
  )
}
